import React, { Fragment, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { get } from 'lodash-es';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { modsRecordPath, MultiOfferModel, searchByVinPath } from 'client/data/models/multi-offer';
import {
  VehicleModel,
  VehicleEntities,
  buildEstimatedStyleIdsPath,
  buildVehicleStylePath,
} from 'client/data/models/vehicle-v2';
import { AppraisalModel, buildAllStylesAppraisalPath, AppraisalEntities } from 'client/data/models/appraisal';
import { INSTANT_OFFER, LOCATION_AND_STYLE } from 'site-modules/shared/constants/appraisal/appraisal';
import { LANDING_URL_PATHS } from 'site-modules/shared/constants/appraisal/paths';
import { getQuery } from 'client/utils/location';
import { ExperimentUtil } from 'client/utils/experiment/experiment-util';
import { makeNiceName } from 'site-modules/shared/utils/nice-name';
import { isUnknownZip } from 'site-modules/shared/utils/location';
import { usePrivacyChoiceTracking } from 'site-modules/shared/hooks/use-privacy-choice-tracking';
import { Experiment, Recipe } from 'site-modules/shared/components/experiment';
import {
  APPRAISER_PRELOAD_EXPERIMENT,
  APPRAISER_PRELOAD_RECIPES,
  getAppraiserStylesPath,
  isAllStylesPreloadEligible,
  selectEmo4182Context,
} from 'client/data/utils/appraiser-preload-experiment';
import { EMO_4379_CAMPAIGN, EMO_4379_RECIPES, isEmo4379Eligible } from 'client/site-modules/appraiser/utils/emo-4379';
import { AppraiserRenewal } from 'client/site-modules/appraiser/pages/appraiser/appraiser-renewal';

import { AppraiserUI } from './appraiser';
import { AppraiserHighlightsUI } from './appraiser-highlights';
import { getSelectedStyleId, transformStylesToStyleIds } from './appraiser-utils';

const useBrowserLayoutEffect = typeof window === 'undefined' ? useEffect : React.useLayoutEffect;

export function AppraiserContainerUI({
  isMobile = false,
  estimatedVehicle = null,
  appraisalStyles = [],
  params,
  location = {},
  isLandingReferer = false,
  isAppExtensionPage = false,
  estimatedAppraisalVin = null,
  modsRecord = null,
  vinModsRecord = null,
  userEmail = '',
  zipCode = '',
  isUnknownVisitorZip = false,
  setModelValue,
  LocationAndStyleStepComponent,
  isEmo2933Chal1 = false,
  isHighlightsPage = false,
  emo4379RecipeFromRedux = null,
}) {
  const [showRenewal, setShowRenewal] = useState(true);
  const [renewalValues, setRenewalValues] = useState(null);
  const appraiserWrapperRef = useRef(null);
  const handleRenewalEdit = () => setShowRenewal(false);

  const handleRenewalSubmit = values => {
    setRenewalValues(values);
    setShowRenewal(false);
  };

  // Scroll to the appropriate step before the renewal screen is removed from the layout.
  useBrowserLayoutEffect(() => {
    if (showRenewal) return;

    const targetStepId = renewalValues ? INSTANT_OFFER : LOCATION_AND_STYLE;
    const targetHeaderSelector = `.trade-in-appraiser .${targetStepId} .step-header`;
    const targetHeader = appraiserWrapperRef.current?.querySelector(targetHeaderSelector);
    if (!targetHeader) return;

    targetHeader.scrollIntoView({ behavior: 'auto', block: 'start' });
  }, [showRenewal, renewalValues]);

  const { styleIds } = getQuery(location);
  const shouldRenderEmo4379 = isEmo4379Eligible(vinModsRecord);
  const isEmo4379Chal1 = emo4379RecipeFromRedux === EMO_4379_RECIPES.CHAL1 && shouldRenderEmo4379;
  const emo4379Recipe = shouldRenderEmo4379 ? emo4379RecipeFromRedux : null;

  usePrivacyChoiceTracking();

  const commonAppraiserProps = {
    isMobile,
    estimatedVehicle,
    appraisalStyles,
    params,
    location,
    isLandingReferer,
    estimatedAppraisalVin,
    modsRecord,
    vinModsRecord,
    userEmail,
    zipCode,
    setModelValue,
    LocationAndStyleStepComponent,
    isEmo2933Chal1,
    emo4379Recipe,
    renewalValues,
  };

  const renderAppraiser = (useGraphqlUsedTmv = false) => {
    const appraiser = (
      <AppraiserUI
        {...commonAppraiserProps}
        useGraphqlUsedTmv={useGraphqlUsedTmv}
        isEmo4379Chal1={isEmo4379Chal1}
        isAppExtensionPage={isAppExtensionPage}
        {...(isEmo4379Chal1 && { isHiddenForRenewal: showRenewal })}
      />
    );

    if (!isEmo4379Chal1) return appraiser;

    return (
      <Fragment>
        {showRenewal && (
          <AppraiserRenewal
            {...commonAppraiserProps}
            isUnknownVisitorZip={isUnknownVisitorZip}
            onEdit={handleRenewalEdit}
            onSubmit={handleRenewalSubmit}
          />
        )}
        <div ref={appraiserWrapperRef} hidden={showRenewal} aria-hidden={showRenewal}>
          {appraiser}
        </div>
      </Fragment>
    );
  };

  return isHighlightsPage ? (
    <AppraiserHighlightsUI {...commonAppraiserProps} />
  ) : (
    <Fragment>
      <Experiment name="EMO-4346" showDefault>
        <Recipe name="ctrl" isDefault>
          {renderAppraiser()}
        </Recipe>
        <Recipe name="chal1">{renderAppraiser(true)}</Recipe>
      </Experiment>
      {shouldRenderEmo4379 && (
        <Experiment name={EMO_4379_CAMPAIGN} showDefault>
          <Recipe name={EMO_4379_RECIPES.CTRL} isDefault />
          <Recipe name={EMO_4379_RECIPES.CHAL1} />
        </Experiment>
      )}
      {isAllStylesPreloadEligible(styleIds) && (
        <Experiment name={APPRAISER_PRELOAD_EXPERIMENT} showDefault>
          <Recipe name={APPRAISER_PRELOAD_RECIPES.CTRL} isDefault />
          <Recipe name={APPRAISER_PRELOAD_RECIPES.CHAL_1} />
        </Experiment>
      )}
      <Experiment name="EMO-3500" showDefault>
        <Recipe name="ctrl" isDefault />
        <Recipe name="chal1" />
      </Experiment>
      <Experiment name="ads-12559" showDefault>
        <Recipe name="ctrl" isDefault />
        <Recipe name="chal" />
      </Experiment>
    </Fragment>
  );
}

AppraiserContainerUI.propTypes = {
  isMobile: PropTypes.bool,
  estimatedVehicle: VehicleEntities.StyleWithoutSubmodels,
  appraisalStyles: AppraisalEntities.Styles,
  params: PropTypes.shape({
    make: PropTypes.string.isRequired,
    model: PropTypes.string.isRequired,
    year: PropTypes.string.isRequired,
  }).isRequired,
  location: PropTypes.shape({}),
  isLandingReferer: PropTypes.bool,
  isAppExtensionPage: PropTypes.bool,
  estimatedAppraisalVin: PropTypes.string,
  modsRecord: PropTypes.shape({}),
  vinModsRecord: PropTypes.shape({}),
  userEmail: PropTypes.string,
  zipCode: PropTypes.string,
  isUnknownVisitorZip: PropTypes.bool,
  setModelValue: PropTypes.func.isRequired,
  LocationAndStyleStepComponent: PropTypes.elementType.isRequired,
  isEmo2933Chal1: PropTypes.bool,
  isHighlightsPage: PropTypes.bool,
  emo4379RecipeFromRedux: PropTypes.string,
};

export const mapStateToProps = state => {
  const isUnknownVisitorZip = isUnknownZip(state.visitor?.location);
  return {
    isMobile: state.mobile,
    userEmail: get(state, 'profile.data.idm.identifiers.email[0]', ''),
    zipCode: isUnknownVisitorZip ? '' : get(state, 'visitor.location.zipCode', ''),
    isUnknownVisitorZip,
    isLandingReferer: LANDING_URL_PATHS.some(path => get(state, 'pageContext.page.referer', '').includes(path)),
    isAppExtensionPage: get(state, 'pageContext.isAppExtensionPage'),
    pageName: get(state, 'pageContext.page.name'),
    emo4182Recipe: selectEmo4182Context(state).emo4182Recipe,
    emo4379RecipeFromRedux: ExperimentUtil.getForcedOrAssignedRecipeName({
      state,
      campaignName: EMO_4379_CAMPAIGN,
      defaultVal: EMO_4379_RECIPES.CTRL,
    }),
  };
};

export const stateToPropsConfig = {
  estimatedVehicle: bindToPath(({ estimatedStyleIds = {}, allStyleIdsForVehicle = [], location = {} }) => {
    const { styleIds } = getQuery(location);
    const selectedStyleId = getSelectedStyleId(estimatedStyleIds, styleIds, allStyleIdsForVehicle);
    return selectedStyleId ? buildVehicleStylePath({ styleId: selectedStyleId }) : '';
  }, VehicleModel),
  appraisalStyles: bindToPath(({ params }) => buildAllStylesAppraisalPath(params), AppraisalModel),
  estimatedAppraisalVin: bindToPath('estimatedAppraisalVin', VehicleModel),
  modsRecord: bindToPath(({ location }) => {
    const { modsId } = getQuery(location);
    return modsId ? modsRecordPath.build(modsId) : '';
  }, MultiOfferModel),
  vinModsRecord: bindToPath(({ location }) => {
    const { vin } = getQuery(location);
    return vin ? searchByVinPath.build(vin) : null;
  }, MultiOfferModel),
};

export const stateToPropsConfigPrefetch = {
  estimatedStyleIds: bindToPath(
    ({ params }) => buildEstimatedStyleIdsPath({ make: params.make, model: params.model, year: params.year }),
    VehicleModel
  ),
  allStyleIdsForVehicle: bindToPath(
    ({ params = {}, location = {}, pageName = '', emo4182Recipe = 'ctrl' }) =>
      getAppraiserStylesPath({
        pageName,
        location,
        emo4182Recipe,
        make: makeNiceName(params.make),
        model: makeNiceName(params.model),
        year: params.year,
      }),
    VehicleModel,
    transformStylesToStyleIds
  ),
};

export const AppraiserContainer = connect(mapStateToProps)(connectToModel(AppraiserContainerUI, stateToPropsConfig));

AppraiserContainer.propTypes = {
  estimatedStyleIds: VehicleEntities.VehicleStyleIds,
  allStyleIdsForVehicle: PropTypes.arrayOf(PropTypes.string),
  params: PropTypes.shape({
    make: PropTypes.string.isRequired,
    model: PropTypes.string.isRequired,
    year: PropTypes.string.isRequired,
  }).isRequired,
  location: PropTypes.shape({}),
  LocationAndStyleStepComponent: PropTypes.elementType.isRequired,
  isHighlightsPage: PropTypes.bool,
};

const mapStateToPropsPrefetch = state => {
  const { pageName, emo4182Recipe } = selectEmo4182Context(state);
  return { pageName, emo4182Recipe };
};

const AppraiserContainerPrefetchModel = connect(mapStateToPropsPrefetch)(
  connectToModel(AppraiserContainer, stateToPropsConfigPrefetch)
);

export const AppraiserContainerPrefetch = props => <AppraiserContainerPrefetchModel {...props} />;
